'use client';

import { Nationwide } from '@ahamove/icons/pages';
import { DeliveryType } from '@ahm/api-wrappers-core';
import { generateLetterAvatar } from '@ahm/common-helpers';
import { useLocationStore } from '@ahm/react-map/store';
import {
  ActionIcon,
  Avatar,
  Badge,
  Button,
  Center,
  Group,
  Popover,
  rem,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { ChevronLeft, InfoIcon } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import BookingServiceInfoTooltip from './booking-service-info-tooltip';
import { useBookingService } from './use-booking-service';
import { ROUTES } from '@/config/router';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useLocaleSuffix } from '@/navigation';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function BookingServiceHeading() {
  const pathname = usePathname();
  const isMobile = useIsMobile();
  const localeSuffix = useLocaleSuffix();
  const tBooking = useTranslations('booking');
  const isLoadingLocation = useLocationStore.use.loading();

  const { isLoadingStore, isOpenPopupAllService } = useBookingStore(
    (state) => state
  );

  const {
    onOpenPopupAllService,
    onClosePopupAllService,
    selectedService,
    pickupPath,
    renderEstimatedServices,
    estimatedServices,
    isLoading,
    isDisabled,
  } = useBookingService({
    enabled: !isMobile,
  });

  const handleViewAllServices = () => {
    if (isOpenPopupAllService) {
      onClosePopupAllService();
      return;
    }
    !isMobile && window.scrollTo({ top: 0, behavior: 'smooth' });
    onOpenPopupAllService();
  };

  const otherServices = useMemo(() => {
    return (
      estimatedServices?.filter((s) => s._id !== selectedService?._id) || []
    );
  }, [estimatedServices, selectedService]);

  const countAvatarGroupLength = useMemo(() => {
    if (pathname === ROUTES.nationwide) {
      return 1;
    }
    return otherServices.length >= 3 ? 3 : otherServices.length;
  }, [pathname, otherServices]);

  const serviceBadge = useMemo(() => {
    const service = estimatedServices?.find((s) => s.badge);
    return service?.badge;
  }, [estimatedServices]);

  if (isMobile) {
    return null;
  }

  if (isLoadingStore) {
    return (
      <Group
        wrap="nowrap"
        gap="md"
        justify="space-between"
        w="100%"
        mih={rem(56)}
      >
        <Group wrap="nowrap" gap={rem(8)} flex={1}>
          <Skeleton height={56} circle flex="none" />
          <Stack gap="sm" flex={1}>
            <Skeleton height={20} w="40%" />
            <Skeleton height={16} w="70%" />
          </Stack>
        </Group>
        <Skeleton height={50} w={166} radius={25} flex="none" />
      </Group>
    );
  }

  if (!pickupPath && !isLoadingLocation) {
    return (
      <Center mih={rem(56)} flex={1}>
        <Group gap="xs" w="100%" p="md" justify="center">
          <InfoIcon width={16} height={16} />
          <Text size="sm" fw={500}>
            {tBooking('service.required_pickup_location')}
          </Text>
        </Group>
      </Center>
    );
  }

  if (!selectedService && !isLoading) {
    return (
      <Center mih={rem(56)} flex={1}>
        <Group gap="xs" w="100%" p="md" justify="center">
          <InfoIcon width={16} height={16} />
          <Text size="sm" fw={500}>
            {tBooking('service.no_data')}
          </Text>
        </Group>
      </Center>
    );
  }

  return (
    <Group
      wrap="nowrap"
      gap="md"
      justify="space-between"
      w="100%"
      mih={rem(56)}
    >
      {selectedService ? (
        <Group wrap="nowrap" gap={rem(8)} flex={1}>
          <Avatar
            variant="outline"
            size={rem(56)}
            p={rem(8)}
            flex="none"
            src={selectedService?.icon_url}
            alt={selectedService?.name}
            styles={{
              root: {
                border: `${rem(2)} solid var(--mantine-color-primary-4)`,
              },
              placeholder: {
                border: 'none',
              },
            }}
          >
            {selectedService.delivery_type === DeliveryType.Nationwide ? (
              <Nationwide width={56} height={56} />
            ) : (
              generateLetterAvatar(selectedService.name)
            )}
          </Avatar>
          <Stack justify="space-between" gap={rem(4)} flex={1} w="200px">
            <Title
              order={4}
              className="flex flex-row flex-nowrap items-center gap-1"
            >
              <span className="truncate">
                {selectedService[`name_${localeSuffix}`]}
              </span>

              <BookingServiceInfoTooltip service={selectedService} />
            </Title>
            <Text
              size={rem(14)}
              className="text-wrap"
              c="neutral.6"
              fw={500}
              lineClamp={1}
            >
              {selectedService[`description_${localeSuffix}`]}
            </Text>
          </Stack>
        </Group>
      ) : isLoading ? (
        <Group wrap="nowrap" gap={rem(8)} flex={1}>
          <Skeleton height={56} circle flex="none" />
          <Stack gap="sm" flex={1}>
            <Skeleton height={20} w="40%" />
            <Skeleton height={16} w="70%" />
          </Stack>
        </Group>
      ) : null}

      {estimatedServices && estimatedServices.length > 1 ? (
        <Popover
          opened={isOpenPopupAllService}
          onChange={onClosePopupAllService}
          styles={{
            dropdown: {
              filter: 'drop-shadow(0px 16px 48px rgba(0, 0, 0, 0.08))',
              borderRadius: 12,
              background: 'white',
              padding: 0,
              border: 'none',
            },
          }}
          zIndex={200}
          width={400}
          offset={16}
          position="right"
          shadow="md"
        >
          <Popover.Target>
            <Button
              disabled={isDisabled}
              onClick={handleViewAllServices}
              maw={rem(208)}
              flex="none"
              variant="light"
              color="neutral"
              radius="xl"
              p={rem(8)}
              h="auto"
              mih={rem(48)}
              ml="auto"
              leftSection={
                otherServices && otherServices.length > 0 ? (
                  <Avatar.Group>
                    {otherServices
                      .slice(0, countAvatarGroupLength)
                      ?.map((s, idx) => (
                        <Avatar
                          variant="outline"
                          src={s.icon_url}
                          size={rem(32)}
                          key={idx}
                          alt={s?.[`name_${localeSuffix}`]}
                          p={rem(7)}
                          styles={{
                            root: {
                              border: `${rem(1)} solid var(--mantine-color-neutral-2)`,
                              zIndex: otherServices.length - idx,
                            },
                            placeholder: {
                              border: 'none',
                            },
                          }}
                        >
                          {generateLetterAvatar(s?.[`name_${localeSuffix}`])}
                        </Avatar>
                      ))}
                  </Avatar.Group>
                ) : undefined
              }
              justify="space-between"
              styles={{
                root: { position: 'relative', overflow: 'visible' },
                label: {
                  width: '100%',
                },
              }}
            >
              {serviceBadge ? (
                <Badge
                  tt="none"
                  size="md"
                  radius="sm"
                  fw={600}
                  pos="absolute"
                  top={rem(-10)}
                  right={0}
                  bg={serviceBadge.background_color ?? 'red.6'}
                  c={serviceBadge.text_color ?? 'white'}
                >
                  {serviceBadge[`description_${localeSuffix}`] ||
                    tBooking('service.view_new_service')}
                </Badge>
              ) : null}
              <Text size="md" fw={700} c="neutral.7">
                {tBooking('service.change_service')}
              </Text>
            </Button>
          </Popover.Target>

          <Popover.Dropdown
            top={0}
            styles={{
              dropdown: {
                overflow: 'hidden',
              },
            }}
          >
            <Group
              pos="sticky"
              top={0}
              left={0}
              justify="flex-start"
              h={rem(60)}
              w="100%"
              p="md"
              bg="white"
              styles={{
                root: {
                  zIndex: 100,
                  borderBottom: `1px solid var(--mantine-color-neutral-1)`,
                },
              }}
            >
              <ActionIcon
                size={rem(34)}
                variant="transparent"
                onClick={onClosePopupAllService}
                c="neutral.5"
              >
                <ChevronLeft width={24} height={24} />
              </ActionIcon>
              <Title lh={rem(28)} order={3}>
                {tBooking('service.choose_service')}
              </Title>
            </Group>
            <ScrollArea.Autosize
              mah={pathname === ROUTES.nationwide ? rem(680 - 65) : '100vh'}
              h="auto"
            >
              {renderEstimatedServices()}
            </ScrollArea.Autosize>
          </Popover.Dropdown>
        </Popover>
      ) : isLoading ? (
        <Skeleton height={50} w={166} radius={25} flex="none" />
      ) : null}
    </Group>
  );
}
